// eslint-disable-next-line no-use-before-define
import React, { useState, useMemo, useContext, Fragment } from 'react'
import { ThemeContext } from 'styled-components'
import { InView } from 'react-intersection-observer'
import { canUseDom } from '../utils'
import { ImageProps, AdditionalMedia } from './types'
import { getSrcSettings, getAdditionalMediaDefaults } from './utilities'

const LazyWrapper = ({ lazy, nativeLazy, lazyThreshold, lazyWrapElement, children }) => {
  const [isInView, toggleInView] = useState(false)
  if (!lazy) return children

  if (!nativeLazy || (nativeLazy && canUseDom() && !('loading' in HTMLImageElement.prototype))) {
    return (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <InView
        as={lazyWrapElement}
        rootMargin='200px 0px'
        threshold={lazyThreshold}
        onChange={(inView: boolean) => toggleInView(inView)}
        triggerOnce
      >
        {isInView && children}
      </InView>
    )
  }
  return children
}

const Image = ({
  src,
  alt,
  width,
  height,
  maxWidth,
  maxHeight,
  quality = 50,
  templates = [],
  responsive = false,
  lazy = false,
  nativeLazy = false,
  lazyThreshold = 0.5,
  lazyWrapElement = 'div',
  className,
  fallbackFmt = 'fmt.jpeg.interlaced=true',
  imageFormat,
  'data-test': dataTest = 'component-image',
  additionalMedia,
  smart,
  noiser = 0,
  cdn = true,
  type,
  ...props
}: ImageProps): React.ReactElement => {
  const theme = useContext(ThemeContext)
  const { src: imageSrc, srcSet, srcSetWebp, srcSetAvif } = getSrcSettings(
    src,
    width,
    height,
    quality,
    templates,
    fallbackFmt,
    cdn,
    noiser,
    maxWidth,
    maxHeight,
    type
  )

  // Map over the additional media (if provided) and build srcSets for each of them.
  // Memoise this, in case there's lots of additional media.
  // When additional media is not provided, the consuming component has an option to pass 'smart' prop
  // smart prop automatically creates additional media based on mobile and tablet breakpoints
  const mediaWithSources = useMemo(() => {
    if ((!Array.isArray(additionalMedia) && !smart) || (!Array.isArray(additionalMedia) && smart && !cdn)) return []

    if (Array.isArray(additionalMedia)) {
      return additionalMedia.map((media) => ({
        sources: getSrcSettings(
          media.src || src,
          media.width || width,
          media.height || height,
          quality,
          media.templates || templates,
          fallbackFmt,
          cdn,
          noiser,
          media.maxWidth || maxWidth,
          media.maxHeight || maxHeight
        ),
        ...media
      }))
    }

    // Below code gets returned if there is NO additional media set & smart is set
    const additionalSmartMedia: AdditionalMedia[] = src
      ? getAdditionalMediaDefaults(theme.size.breakpoint, src, maxWidth)
      : undefined

    return (
      additionalSmartMedia.map((media) => ({
        sources: getSrcSettings(
          media.src || src,
          media.width || width,
          media.height || height,
          quality,
          media.templates || templates,
          fallbackFmt,
          cdn,
          noiser,
          media.maxWidth || maxWidth,
          media.maxHeight || maxHeight
        ),
        // this is to differentiate between smart sources and normal sources with the help of data-test line #112
        smart,
        ...media
      })) || []
    )
  }, [additionalMedia, smart])

  return (
    <LazyWrapper lazy={lazy} nativeLazy={nativeLazy} lazyThreshold={lazyThreshold} lazyWrapElement={lazyWrapElement}>
      <picture>
        {mediaWithSources.map((media, index) => (
          <Fragment key={`${media.sources.srcSet}-${index}`}>
            {imageFormat === 'avif' && (
              <source
                media={media.query}
                type='image/avif'
                srcSet={media.sources.srcSetAvif}
                data-test={`${dataTest}-avif-source-${index}`}
              />
            )}
            <source
              media={media.query}
              type='image/webp'
              srcSet={media.sources.srcSetWebp}
              data-test={`${dataTest}-webp-source-${index}`}
            />
            <source
              media={media.query}
              srcSet={media.sources.srcSet}
              data-test={media.smart ? `${dataTest}-smart-source-${index}` : `${dataTest}-source-${index}`}
            />
          </Fragment>
        ))}
        {imageFormat === 'avif' && (
          <source type='image/avif' srcSet={srcSetAvif} data-test={`${dataTest}-avif-source`} />
        )}
        {srcSetWebp && <source type='image/webp' srcSet={srcSetWebp} />}
        <source srcSet={srcSet} />
        <img
          src={imageSrc}
          width={responsive ? '100%' : width}
          height={responsive ? '100%' : height}
          alt={alt}
          data-test={dataTest}
          className={className}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          loading={lazy && nativeLazy ? 'lazy' : 'auto'}
          {...props}
        />
      </picture>
    </LazyWrapper>
  )
}

export default Image
